import { Box, Chip, Typography, Collapse, IconButton } from '@mui/material';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import { useState } from 'react';

interface ToolCallIndicatorProps {
  toolName: string;
  arguments: string;
  status?: 'pending' | 'executing' | 'completed' | 'failed';
  result?: string;
}

/**
 * Component for displaying tool call information.
 */
export const ToolCallIndicator = ({
  toolName,
  arguments: args,
  status = 'pending',
  result,
}: ToolCallIndicatorProps) => {
  const [expanded, setExpanded] = useState(false);

  const getStatusColor = () => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'failed':
        return 'error';
      case 'executing':
        return 'info';
      default:
        return 'default';
    }
  };

  const getStatusLabel = () => {
    switch (status) {
      case 'completed':
        return 'Completed';
      case 'failed':
        return 'Failed';
      case 'executing':
        return 'Executing...';
      default:
        return 'Pending';
    }
  };

  return (
    <Box
      sx={{
        p: 1.5,
        mb: 1,
        bgcolor: 'action.hover',
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Chip
          label={`Tool: ${toolName}`}
          size="small"
          color={getStatusColor()}
          variant="outlined"
        />
        <Chip label={getStatusLabel()} size="small" color={getStatusColor()} />
        <Box sx={{ flexGrow: 1 }} />
        <IconButton size="small" onClick={() => setExpanded(!expanded)}>
          {expanded ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      </Box>
      <Collapse in={expanded}>
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
            Arguments:
          </Typography>
          <Typography
            variant="body2"
            component="pre"
            sx={{
              p: 1,
              bgcolor: 'background.paper',
              borderRadius: 1,
              overflow: 'auto',
              fontSize: '0.75rem',
              fontFamily: 'monospace',
            }}
          >
            {args}
          </Typography>
          {result && (
            <>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }} gutterBottom>
                Result:
              </Typography>
              <Typography
                variant="body2"
                component="pre"
                sx={{
                  p: 1,
                  bgcolor: 'background.paper',
                  borderRadius: 1,
                  overflow: 'auto',
                  fontSize: '0.75rem',
                  fontFamily: 'monospace',
                }}
              >
                {result}
              </Typography>
            </>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};
